import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import AdminSidebar from '@/components/AdminSidebar';
import EditorSidebar from '@/components/EditorSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { FileText, Eye, Trash2, Calendar, User, CheckCircle, XCircle, PenTool } from 'lucide-react';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { isAdmin } from '@/utils/roleUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import EditorDashboardBlog from './EditorDashboardBlog';

// Fonction pour formater le contenu markdown
const formatContent = (content: string) => {
  return content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
    .replace(/^• (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$1. $2</li>')
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-primary pl-4 italic text-muted-foreground">$1</blockquote>')
    .replace(/\n/g, '<br/>');
};
interface BlogPost {
  id: string;
  title: string;
  summary: string;
  content?: string;
  category?: string;
  reading_time?: number;
  image_url?: string;
  author_id?: string;
  author?: string;
  created_at: string;
  updated_at: string;
  published_date?: string;
  status: string;
  validated_by?: string;
}
const DashboardBlog = () => {
  const {
    user
  } = useAuth();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingArticle, setViewingArticle] = useState<BlogPost | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    article: BlogPost | null;
  }>({
    isOpen: false,
    article: null
  });
  if (!user) {
    return <div>Non autorisé</div>;
  }

  // Si l'utilisateur n'est pas admin, afficher le dashboard rédacteur
  if (!isAdmin(user)) {
    return <EditorDashboardBlog />;
  }
  useEffect(() => {
    fetchPosts();
  }, []);
  const fetchPosts = async () => {
    try {
      setLoading(true);
      const {
        data,
        error
      } = await supabase.from('blog_articles').select('*').order('created_at', {
        ascending: false
      });
      if (error) {
        throw error;
      }

      // Transform data to match BlogPost interface
      const transformedPosts = (data || []).map(post => {
        return {
          ...post,
          author: post.author_name || 'Auteur inconnu',
          published_date: post.published_date || post.created_at
        };
      });
      setPosts(transformedPosts);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewArticle = (post: BlogPost) => {
    setViewingArticle(post);
  };
  const handleDeleteArticle = (post: BlogPost) => {
    setDeleteConfirm({
      isOpen: true,
      article: post
    });
  };
  const confirmDeleteArticle = async () => {
    if (!deleteConfirm.article) return;
    try {
      const {
        error
      } = await supabase.from('blog_articles').delete().eq('id', deleteConfirm.article.id);
      if (error) throw error;
      toast.success('Article supprimé avec succès');
      setDeleteConfirm({
        isOpen: false,
        article: null
      });
      fetchPosts(); // Actualiser la liste après suppression
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Erreur lors de la suppression de l\'article');
    }
  };
  const handleToggleStatus = async (post: BlogPost) => {
    try {
      let newStatus: 'en_attente' | 'valide' | 'refuse';
      switch (post.status) {
        case 'en_attente':
          newStatus = 'valide';
          break;
        case 'valide':
          newStatus = 'en_attente';
          break;
        case 'refuse':
          newStatus = 'valide';
          break;
        default:
          newStatus = 'en_attente';
      }
      const {
        error
      } = await supabase.from('blog_articles').update({
        status: newStatus,
        validated_by: newStatus === 'valide' ? user?.id : null,
        updated_at: new Date().toISOString()
      }).eq('id', post.id);
      if (error) throw error;
      const statusText = newStatus === 'valide' ? 'publié' : newStatus === 'en_attente' ? 'mis en attente' : 'refusé';
      toast.success(`Article ${statusText} avec succès`);
      fetchPosts(); // Actualiser la liste après changement de statut
    } catch (error) {
      console.error('Error toggling article status:', error);
      toast.error('Erreur lors du changement de statut de l\'article');
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'en_attente':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">En attente</span>;
      case 'refuse':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm">Refusé</span>;
      case 'valide':
      case 'publie':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Publié</span>;
      default:
        return null;
    }
  };
  const getToggleButtonText = (status: string) => {
    switch (status) {
      case 'en_attente':
        return 'Publier';
      case 'valide':
      case 'publie':
        return 'Dépublier';
      case 'refuse':
        return 'Publier';
      default:
        return 'Publier';
    }
  };
  if (loading) {
    return <Layout>
        <div className={isMobile ? "px-[25px] py-[50px] pb-20" : isTablet ? "px-[30px] py-[40px] pb-20" : "flex"}>
          {!isMobile && !isTablet && <AdminSidebar />}
          <div className={isMobile ? "" : isTablet ? "" : "flex-1 ml-64 p-8"}>
            <div className="text-center">Chargement...</div>
          </div>
        </div>
        {(isMobile || isTablet) && <AdminSidebar />}
      </Layout>;
  }
  if (isMobile) {
    return <Layout>
        <div className="px-[25px] py-[50px] pb-20">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary">Gestion du blog</h1>
            <p className="text-gray-600 mt-1 text-sm">Tous les articles de blog des rédacteurs</p>
          </div>

          <div className="mb-4">
            <h2 className="text-lg font-semibold text-primary flex items-center">
              <PenTool className="mr-2 h-5 w-5" />
              Liste des articles de blog ({posts.length})
            </h2>
          </div>

          <div className="space-y-4">
            {posts.length === 0 ? <p className="text-center text-gray-500 py-8 text-sm">
                Aucun article de blog soumis
              </p> : posts.map(post => <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                  {post.image_url && <div className="h-32">
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>}
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-primary text-white">
                        {post.category || 'Non catégorisé'}
                      </span>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(post.published_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <h3 className="font-semibold text-lg text-primary mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">
                      Par <span className="font-medium text-primary">{post.author}</span>
                    </p>
                    {post.summary && <p className="text-xs text-gray-700 mb-3 line-clamp-3">{post.summary}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {getStatusBadge(post.status)}
                      </div>
                      <div className="flex space-x-1">
                        <Button variant="outline" size="sm" onClick={() => handleViewArticle(post)}>
                          <Eye className="h-3 w-3" />
                        </Button>
                        
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteArticle(post)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
          </div>
        </div>
        <AdminSidebar />
      </Layout>;
  }
  if (isTablet) {
    return <Layout>
        <div className="px-[30px] py-[40px] pb-20">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">Gestion du blog</h1>
            <p className="text-gray-600 mt-2">Tous les articles de blog des rédacteurs</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-primary flex items-center">
              <PenTool className="mr-2 h-5 w-5" />
              Liste des articles de blog ({posts.length})
            </h2>
          </div>

          <div className="space-y-4">
            {posts.length === 0 ? <p className="text-center text-gray-500 py-8">
                Aucun article de blog créé
              </p> : posts.map(post => <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                  {post.image_url && <div className="h-25">
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>}
                  <CardContent className="p-5">
                    <div className="flex justify-between items-center mb-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-primary text-white">
                        {post.category || 'Non catégorisé'}
                      </span>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(post.published_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <h3 className="font-semibold text-xl text-primary mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Par <span className="font-medium text-primary">{post.author}</span>
                    </p>
                    {post.summary && <p className="text-sm text-gray-600 mb-4 line-clamp-3">{post.summary}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {getStatusBadge(post.status)}
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewArticle(post)}>
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleStatus(post)} className={post.status === 'valide' || post.status === 'publie' ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}>
                          {post.status === 'valide' || post.status === 'publie' ? <XCircle className="h-4 w-4 mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                          {getToggleButtonText(post.status)}
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteArticle(post)}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
          </div>
        </div>
        <AdminSidebar />
      </Layout>;
  }
  return <Layout>
      <div className="flex">
        <AdminSidebar />
        
        <div className="flex-1 ml-64 p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">Gestion du blog</h1>
            <p className="text-gray-600 mt-2">Tous les articles de blog des rédacteurs</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-primary flex items-center">
              <PenTool className="mr-2 h-5 w-5" />
              Liste des articles de blog ({posts.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.length === 0 ? <p className="text-center text-gray-500 py-8">
                Aucun article de blog créé
              </p> : posts.map(post => <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                  {post.image_url && <div className="h-32 md:h-48">
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>}
                  <CardContent className="p-4 md:p-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-primary text-white">
                        {post.category || 'Non catégorisé'}
                      </span>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(post.published_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <h3 className="font-semibold text-xl text-primary mb-2 md:mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Par <span className="font-medium text-primary">{post.author}</span>
                    </p>
                    {post.summary && <p className="text-gray-600 mb-3 md:mb-4 line-clamp-3 text-sm md:text-base">{post.summary}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {getStatusBadge(post.status)}
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="default" onClick={() => handleViewArticle(post)} className="flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          Voir
                        </Button>
                        
                        <Button variant="outline" size="default" className="text-red-600 hover:text-red-700 flex items-center gap-2" onClick={() => handleDeleteArticle(post)}>
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
          </div>
        </div>
      </div>
      
      {/* Dialog pour voir un article */}
      <Dialog open={!!viewingArticle} onOpenChange={() => setViewingArticle(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingArticle?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Par {viewingArticle?.author}</span>
              <span>{viewingArticle?.published_date && new Date(viewingArticle.published_date).toLocaleDateString('fr-FR')}</span>
              {getStatusBadge(viewingArticle?.status || '')}
            </div>
            {viewingArticle?.image_url && <img src={viewingArticle.image_url} alt={viewingArticle.title} className="w-full h-64 object-cover rounded-lg" />}
            <div className="prose max-w-none">
              <p className="text-lg text-muted-foreground">{viewingArticle?.summary}</p>
              <div 
                className="mt-4"
                dangerouslySetInnerHTML={{
                  __html: formatContent(viewingArticle?.content || '')
                }} 
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteConfirm.isOpen} onOpenChange={isOpen => !isOpen && setDeleteConfirm({
      isOpen: false,
      article: null
    })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'article "{deleteConfirm.article?.title}" ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirm({
            isOpen: false,
            article: null
          })}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteArticle} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>;
};
export default DashboardBlog;